import { trpc } from '@/api/trpc';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import { useAppQuery } from '@/hooks/useAppQuery';
import { Button, Card } from '@/components/UI';

export default function FuturesSignalRoutesCard({ strategyId, canEdit }: { strategyId: number; canEdit: boolean }) {
    const catalog = useAppQuery(trpc.strategy.fetchFuturesSignalRouteCatalog.queryOptions());
    const routes = useAppQuery(trpc.strategy.fetchFuturesSignalRoutes.queryOptions({ strategyId }));
    const create = useTrpcMutation(trpc.strategy.createFuturesSignalRoute.mutationOptions(), {
        fallbackErrorMessage: 'Unable to save the ETF futures route'
    });
    return (
        <Card
            title="ETF signals → futures execution"
            subtitle="Historical research only. Save an inactive, regular-session 1m stock strategy with automatic backtesting off before adding a route."
        >
            <p className="mb-3 text-sm text-zinc-400">
                The universe screener qualifies the ETF. Entry rules read its completed bars. One Future leg trades the mapped product using
                dated futures prices. Exits use futures prices and elapsed futures bars. Saved routes are immutable.
            </p>
            <div className="flex flex-wrap gap-3">
                {catalog.data?.map((pair) => {
                    const saved = routes.data?.find(
                        (route) => route.signalAssetId === pair.signalAssetId && route.productId === pair.productId
                    );
                    return (
                        <Button
                            key={pair.signalAssetId}
                            disabled={!canEdit || create.isPending || routes.isPending || !!saved}
                            onClick={async () => {
                                try {
                                    await create.mutateAsync({ strategyId, signalAssetId: pair.signalAssetId, productId: pair.productId });
                                    await routes.refetch();
                                } catch {
                                    /* Mutation displays the error. */
                                }
                            }}
                        >
                            {pair.signalSymbol} → {pair.productSymbol}
                            {saved ? ' · saved' : ' · add route'}
                        </Button>
                    );
                })}
            </div>
        </Card>
    );
}
